import { useState } from "react";

import {
  Alert,
  Button,
  FormField,
  Modal,
  TextInput,
  ToggleInput,
  useToast,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import type { PaymentGatewayConnectionView } from "@/lib/api/types";
import {
  useDisconnectPaymentGatewayMutation,
  usePaymentGatewayConnectionQuery,
  useSavePaymentGatewayMutation,
  useUpdatePaymentGatewayActiveMutation,
} from "@/modules/settings/services/usePaymentGatewayConnection";
import {
  buildGatewayConfig,
  type GatewayDescriptor,
} from "./paymentGatewayDescriptors";
import { GatewayLogo } from "./logos/GatewayLogo";

type T = ReturnType<typeof useTranslations>;

type Props = {
  storeSlug: string;
  descriptor: GatewayDescriptor;
  isOpen: boolean;
  onClose: () => void;
};

export function PaymentGatewaySheet({
  storeSlug,
  descriptor,
  isOpen,
  onClose,
}: Props) {
  const t = useTranslations();
  const { connection, isLoading } = usePaymentGatewayConnectionQuery({
    storeSlug,
    provider: descriptor.provider,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      closeLabel={t(keys.common.close)}
      hideFooter
      headerOverride={
        <div className="flex items-center gap-2.5">
          <GatewayLogo descriptor={descriptor} size="sm" />
          <h3 className="text-base font-semibold text-[color:var(--text-primary)]">
            {t(descriptor.brandKey)}
          </h3>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex flex-col gap-4 p-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-xl bg-[color:var(--surface-alt)]"
            />
          ))}
        </div>
      ) : connection ? (
        <ConnectedView
          storeSlug={storeSlug}
          descriptor={descriptor}
          connection={connection}
          onDisconnected={onClose}
          t={t}
        />
      ) : (
        <SetupView storeSlug={storeSlug} descriptor={descriptor} t={t} />
      )}
    </Modal>
  );
}

function SetupView({
  storeSlug,
  descriptor,
  t,
}: {
  storeSlug: string;
  descriptor: GatewayDescriptor;
  t: T;
}) {
  const toast = useToast();
  const { mutateAsync: save, isPending } = useSavePaymentGatewayMutation(
    descriptor.provider,
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isComplete = descriptor.fields.every(
    (f) => f.name === "region" || (values[f.name] ?? "").trim().length > 0,
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    try {
      await save({ storeSlug, config: buildGatewayConfig(descriptor, values) });
      toast.success(t(keys.paymentGateway.savedNotice));
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-[520px] flex-col gap-6">
          <p className="text-sm leading-relaxed text-[color:var(--text-secondary)]">
            {t(keys.paymentGateway.setupIntro)}
          </p>
          {submitError ? <Alert message={submitError} /> : null}
          <div className="flex flex-col gap-4">
            {descriptor.fields.map((field) => (
              <FormField
                key={field.name}
                label={t(field.labelKey)}
                name={field.name}
              >
                {field.type === "mode" ? (
                  <select
                    name={field.name}
                    value={values[field.name] ?? "live"}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [field.name]: e.target.value }))
                    }
                    className="h-10 w-full rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-sm text-[color:var(--text-primary)]"
                  >
                    <option value="live">
                      {t(keys.paymentGateway.modeLive)}
                    </option>
                    <option value="test">
                      {t(keys.paymentGateway.modeTest)}
                    </option>
                  </select>
                ) : (
                  <TextInput
                    name={field.name}
                    type={field.type === "password" ? "password" : "text"}
                    value={values[field.name] ?? ""}
                    autoComplete="off"
                    dir="ltr"
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [field.name]: e.target.value }))
                    }
                  />
                )}
              </FormField>
            ))}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end border-t border-[color:var(--border-light)] px-4 py-3 sm:px-6">
        <Button
          type="submit"
          variant="primary"
          disabled={isPending || !isComplete}
          className="h-10 rounded-lg px-6 text-sm font-semibold disabled:opacity-60"
        >
          {isPending
            ? t(keys.paymentGateway.savingAction)
            : t(keys.paymentGateway.saveAction)}
        </Button>
      </div>
    </form>
  );
}

function ConnectedView({
  storeSlug,
  descriptor,
  connection,
  onDisconnected,
  t,
}: {
  storeSlug: string;
  descriptor: GatewayDescriptor;
  connection: PaymentGatewayConnectionView;
  onDisconnected: () => void;
  t: T;
}) {
  const toast = useToast();
  const { mutateAsync: disconnect, isPending } =
    useDisconnectPaymentGatewayMutation(descriptor.provider);
  const { mutateAsync: updateActive, isPending: isToggling } =
    useUpdatePaymentGatewayActiveMutation(descriptor.provider);
  const [confirming, setConfirming] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleToggleActive = async (next: boolean) => {
    setSubmitError(null);
    try {
      await updateActive({ storeSlug, isActive: next });
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  const handleCopy = async (value: string) => {
    if (await copyTextToClipboard(value)) {
      toast.success(t(keys.paymentGateway.copiedNotice));
    }
  };

  const handleDisconnect = async () => {
    setSubmitError(null);
    try {
      await disconnect({ storeSlug });
      toast.success(t(keys.paymentGateway.disconnectedNotice));
      onDisconnected();
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        <div className="mx-auto flex w-full max-w-[520px] flex-col gap-6">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-4 py-3">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    connection.isActive
                      ? "bg-[color:var(--success-500,#16A34A)]"
                      : "bg-amber-500"
                  }`}
                />
                <span className="text-sm font-semibold text-[color:var(--text-primary)]">
                  {t(keys.paymentGateway.activeLabel)}
                </span>
              </div>
              <p className="text-xs leading-relaxed text-[color:var(--text-muted)]">
                {t(keys.paymentGateway.activeHelper)}
              </p>
            </div>
            <ToggleInput
              name={`${descriptor.provider}-active-sheet`}
              checked={connection.isActive}
              disabled={isToggling}
              onChange={(event) => handleToggleActive(event.target.checked)}
              ariaLabel={t(keys.paymentGateway.activeLabel)}
            />
          </div>

          {connection.lastError ? (
            <Alert message={connection.lastError} />
          ) : null}
          {submitError ? <Alert message={submitError} /> : null}

          <div className="flex flex-col gap-2">
            <h4 className="text-sm font-semibold text-[color:var(--text-primary)]">
              {t(keys.paymentGateway.webhookSectionTitle)}
            </h4>
            <p className="text-xs leading-relaxed text-[color:var(--text-muted)]">
              {t(keys.paymentGateway.webhookSectionBody)}
            </p>
            <div className="flex items-stretch overflow-hidden rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface-alt)]">
              <code
                className="flex-1 truncate px-3 py-2.5 font-mono text-xs text-[color:var(--text-primary)]"
                dir="ltr"
              >
                {connection.webhookUrl}
              </code>
              <button
                type="button"
                onClick={() => void handleCopy(connection.webhookUrl)}
                className="shrink-0 border-s border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-xs font-semibold text-[color:var(--text-secondary)] transition hover:text-[color:var(--text-primary)]"
              >
                {t(keys.paymentGateway.copyAction)}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-[color:var(--border-light)] px-4 py-3 sm:px-6">
        {confirming ? (
          <div className="flex flex-1 items-center justify-between gap-3">
            <span className="text-xs text-[color:var(--text-secondary)]">
              {t(keys.paymentGateway.disconnectConfirmBody)}
            </span>
            <div className="flex shrink-0 gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => setConfirming(false)}
                className="h-10 rounded-lg px-4 text-sm font-semibold"
              >
                {t(keys.paymentGateway.cancelAction)}
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={isPending}
                onClick={() => void handleDisconnect()}
                className="h-10 rounded-lg px-4 text-sm font-semibold"
              >
                {isPending
                  ? t(keys.common.loading)
                  : t(keys.paymentGateway.disconnectConfirmCta)}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="text-sm font-semibold text-[color:var(--error-500)] transition hover:text-[color:var(--error-600,#8B2525)]"
          >
            {t(keys.paymentGateway.disconnectAction)}
          </button>
        )}
      </div>
    </>
  );
}
